import { useEffect } from 'react'
import Router from 'next/router'

function saveScrollPos(url) {
  const scrollPos = { x: window.scrollX, y: window.scrollY }
  // Use a try/catch block for safer sessionStorage access
  try {
    sessionStorage.setItem(url, JSON.stringify(scrollPos))
  } catch (error) {
    console.error('Failed to save scroll position to session storage:', error)
  }
}

function restoreScrollPos(url) {
  try {
    const scrollPos = JSON.parse(sessionStorage.getItem(url))
    if (scrollPos) {
      // Use setTimeout to ensure DOM is stable before scrolling
      setTimeout(() => {
        window.scrollTo(scrollPos.x, scrollPos.y)
      }, 50) // Small delay for better UX
    }
  } catch (error) {
    console.error('Failed to restore scroll position:', error)
  }
}

export default function useScrollRestoration(router) {
  useEffect(() => {
    // 1. Feature check on mount
    if (!('scrollRestoration' in window.history)) {
      return
    }

    let shouldScrollRestore = false
    window.history.scrollRestoration = 'manual'

    // Initial restoration delay for first page load (see point 1 above)
    const initialRestoreTimer = setTimeout(() => {
      restoreScrollPos(router.asPath)
    }, 100)

    const onBeforeUnload = (event) => {
      saveScrollPos(router.asPath)
      delete event['returnValue']
    }

    const onRouteChangeStart = () => {
      saveScrollPos(router.asPath)
    }

    const onRouteChangeComplete = (url) => {
      if (shouldScrollRestore) {
        shouldScrollRestore = false
        // The restoreScrollPos function now includes a small delay.
        restoreScrollPos(url)
      }
    }

    // 2. Attach Events
    window.addEventListener('beforeunload', onBeforeUnload)
    Router.events.on('routeChangeStart', onRouteChangeStart)
    Router.events.on('routeChangeComplete', onRouteChangeComplete)
    Router.beforePopState(() => {
      shouldScrollRestore = true
      return true
    })

    // 3. Cleanup on unmount
    return () => {
      clearTimeout(initialRestoreTimer)
      window.removeEventListener('beforeunload', onBeforeUnload)
      Router.events.off('routeChangeStart', onRouteChangeStart)
      Router.events.off('routeChangeComplete', onRouteChangeComplete)
      // Restore default behavior
      Router.beforePopState(() => true)
    }
  }, [router.asPath]) // Added router.asPath to dependency array to handle initial load
}
